/**
 * Cooling System edit body for the descriptor engine (#997 Story 6.6, mirror of
 * `LargeUpsEngineEditFormBody`).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `CoolingSystemEditFormContent` — the user-approved Cooling editor (field set,
 * order, labels, placeholders, `FormTwoOptionChoice` type toggle, the
 * NumberInput temp-rise control, BOTH per-unit attachment carousels — details
 * `COOLING_READINESS_ADVANCED` + additional-info `COOLING_READINESS_ADDITIONAL`,
 * and the Additional-Info card) — bound to the engine's edit-session state. The
 * engine owns list/save/flush/attachment lifecycle (the 6.1/6.2 fixes), the
 * section owns its view.
 *
 * Divergence from `SmallUpsEngineEditFormBody`: `CoolingSystemEditFormContent`
 * gates its Additional-Info card behind `showAdditionalInformation`, so the host
 * threads it (Cooling always passes it true) + `showFullDetails` through the
 * `editBody` closure — same shape as `LargeUpsEngineEditFormBody`.
 *
 * Binding contract:
 * - `formData` is COOLING-shaped: the engine's editor field map is coerced back
 *   through `descriptorFieldMapToPatch`, plus identity (`name` ← `instanceName`,
 *   `status` ← `instanceStatus`) and the unit id (`id` ← `configurationId`, which
 *   `CoolingSystemEditFormContent` uses to scope its two per-unit attachment
 *   cards — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField`, converting bespoke values to the engine's
 *   editor forms (undefined → '', boolean kept). The temp-rise field passes
 *   through as a string — the descriptor types it `text` (stored NVARCHAR), so no
 *   number coercion false-dirties untouched units.
 */

import React, { useCallback, useMemo } from 'react';

import { CoolingSystemEditFormContent } from '../../../../components/coolingsystems/CoolingSystemEditFormContent';
import { normalizeCoolingSystemStatus } from '../../../../components/coolingsystems/coolingSystemListUtils';
import { COOLING_SYSTEM_DESCRIPTOR } from '../../../sections/coolingSystem.descriptor';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { CoolingSystemConfiguration } from '../../../../types/coolingSystem';

export type CoolingSystemEngineEditFormBodyProps =
  RepeatableInstanceEditFormRenderProps & {
    /** Cooling gates its Additional-Info card (mirror Large UPS). */
    showAdditionalInformation?: boolean;
    showFullDetails?: boolean;
  };

export function CoolingSystemEngineEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  showAdditionalInformation,
  showFullDetails,
}: CoolingSystemEngineEditFormBodyProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(COOLING_SYSTEM_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<CoolingSystemConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<CoolingSystemConfiguration>),
      id: configurationId,
      name: instanceName ?? (patchName as string | undefined),
      status: normalizeCoolingSystemStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof CoolingSystemConfiguration>(
      field: K,
      value: CoolingSystemConfiguration[K],
    ) => {
      if (field === 'name') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'status') {
        const status = normalizeCoolingSystemStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // a cleared value becomes blank — never 'undefined'. The temp-rise number
      // control writes a string; passed through as-is (descriptor type=text).
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <CoolingSystemEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
      showAdditionalInformation={showAdditionalInformation}
      showFullDetails={showFullDetails}
      onSharedFieldDirty={markDirty}
    />
  );
}
